package com.evoapps.evoshift.saves

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.SnackbarDuration
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.SnackbarResult
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.remember
import com.evoapps.evoshift.ui.theme.rememberLastNonNull
import com.evoapps.evoshift.ui.theme.MotionVisibility
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.LiveRegionMode
import androidx.compose.ui.semantics.liveRegion
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.asString
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.saves_kept_on_device
import evoshift.composeapp.generated.resources.saves_recalculating
import evoshift.composeapp.generated.resources.saves_retry
import evoshift.composeapp.generated.resources.saves_saving
import org.jetbrains.compose.resources.stringResource

/** The app's [PendingSaves] (provided by the shell); null in previews and screen-only tests. */
val LocalPendingSaves = staticCompositionLocalOf<PendingSaves?> { null }

object SavesTags {
    const val INDICATOR = "saves_indicator"
}

/**
 * SH-148: the app-wide "Saving…" line under the top bar's row while any save runs
 * ("Recalculating costs…" when only background recalculations do). Nothing otherwise.
 * SH-158: it unfolds and folds away (the bar grows smoothly instead of jumping), keeping its last
 * words while it goes.
 */
@Composable
fun SavingIndicator(color: Color, modifier: Modifier = Modifier) {
    val saves = LocalPendingSaves.current ?: return
    val all by saves.saves.collectAsState()
    val running = all.filter { it.isRunning }
    val now = when {
        running.isEmpty() -> null
        running.any { it.kind == SaveKind.SAVE } -> Res.string.saves_saving
        else -> Res.string.saves_recalculating
    }
    // What it said last, while it folds away.
    val labelRes = rememberLastNonNull(now) ?: Res.string.saves_saving
    MotionVisibility(visible = now != null, modifier = modifier.fillMaxWidth()) {
        val label = stringResource(labelRes)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(start = 16.dp, end = 16.dp, bottom = 4.dp)
                .semantics(mergeDescendants = true) { liveRegion = LiveRegionMode.Polite }
                .testTag(SavesTags.INDICATOR),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically
        ) {
            CircularProgressIndicator(modifier = Modifier.size(12.dp), color = color, strokeWidth = 1.5.dp)
            Spacer(Modifier.width(6.dp))
            Text(label, color = color, fontSize = 12.sp, fontWeight = FontWeight.SemiBold, maxLines = 1)
        }
    }
}

/**
 * SH-148: the shell's snackbar for saves that ended after their screen was left: the success
 * message, or the failure with "Retry" (dismissed: the save is forgotten). SH-186: and for a save
 * its screen stopped waiting for because the device is offline ("Saved on this device").
 */
@Composable
fun SaveNoticesHost(saves: PendingSaves, modifier: Modifier = Modifier) {
    val hostState = remember { SnackbarHostState() }
    val queue = remember { mutableStateListOf<SaveNotice>() }
    LaunchedEffect(saves) { saves.notices.collect { queue.add(it) } }
    queue.firstOrNull()?.let { notice ->
        val text = notice.message().asString()
        val retry = stringResource(Res.string.saves_retry)
        LaunchedEffect(notice) {
            val failed = notice as? SaveNotice.Failed
            val result = hostState.showSnackbar(
                message = text,
                actionLabel = if (failed != null) retry else null,
                withDismissAction = failed != null,
                duration = if (failed != null) SnackbarDuration.Long else SnackbarDuration.Short
            )
            if (failed != null) {
                if (result == SnackbarResult.ActionPerformed) saves.retry(failed.id) else saves.dismiss(failed.id)
            }
            queue.remove(notice)
        }
    }
    SnackbarHost(hostState, modifier)
}

private fun SaveNotice.message() = when (this) {
    is SaveNotice.Saved -> message
    is SaveNotice.Failed -> message
    SaveNotice.KeptOnDevice -> UiText.Resource(Res.string.saves_kept_on_device)
}
